
// src/navigation.data.ts

// This file contains the raw, non-React navigation structure.
// It is safe to import into both the frontend and backend (Node.js) environments.

export interface NavigationItem {
    text: string;
    path: string;
    navId: string;
    subItems?: Partial<NavigationItem>[];
}

export const navigationData: NavigationItem[] = [
    { text: 'Dashboard', path: '/dashboard', navId: 'dashboard' },
    { 
        text: 'Skills', 
        path: '/skills', 
        navId: 'skills',
        subItems: [
            { text: 'Sales Simulator', path: '/skills/sales-simulator', navId: 'sales-simulator' },
            { text: 'Certification Paths', path: '/skills/certification-paths', navId: 'certification-paths' },
            { text: 'Deep Dive', path: '/skills/deep-dive', navId: 'deep-dive' }
        ]
    },
    { 
        text: 'Advisor', 
        path: '/advisor', 
        navId: 'advisor',
        subItems: [
            { text: 'Growth Planner', path: '/advisor/partner-growth-planner', navId: 'partner-growth-planner' },
            { text: 'Strategic Account Planner', path: '/advisor/strategic-account-planner', navId: 'strategic-account-planner' },
            { text: 'Solution Navigator', path: '/advisor/synapse-solutioning', navId: 'solution-navigator' },
            { text: 'North America Solution Builder', path: '/advisor/na-solution-builder', navId: 'na-solution-builder' },
            { text: 'Researcher', path: '/advisor/synapse-researcher', navId: 'synapse-researcher' },
            { text: 'Partner Intelligence', path: '/advisor/partner-intelligence', navId: 'partner-intelligence' }
        ]
    },
    { 
        text: 'Studio', 
        path: '/studio',
        navId: 'studio',
        subItems: [
            { text: 'Content Workshop', path: '/studio/content-workshop', navId: 'content-workshop' },
            { text: 'Global Voice', path: '/studio/global-voice', navId: 'global-voice' },
            { text: 'Image Generation', path: '/studio/synapse-imagen', navId: 'synapse-imagen' },
            { text: 'Scenario Generator', path: '/studio/scenario-generator', navId: 'scenario-generator' },
            { text: 'Presentation Builder', path: '/studio/presentation-builder', navId: 'presentation-builder' },
            { text: 'Course Management', path: '/studio/course-management', navId: 'course-management' },
            { text: 'News Management', path: '/studio/news-management', navId: 'news-management' }
        ]
    },
    { text: 'Library', path: '/library', navId: 'library' },
    { text: 'Assist', path: '/assist', navId: 'assist' },
    { 
        text: 'Vendor Hub', 
        path: '/vendor-hub', 
        navId: 'vendor-hub',
        subItems: [
            { text: 'Google Deal Analyzer', path: '/vendor-hub/google/deal-analyzer', navId: 'google-deal-analyzer' },
            { text: 'Microsoft Enablement', path: '/vendor-hub/microsoft/enablement', navId: 'microsoft-enablement' },
            { text: 'Microsoft Licensing Coach', path: '/vendor-hub/microsoft/licensing-coach', navId: 'microsoft-licensing-coach' }
        ]
    },
    { 
        text: 'Admin Console', 
        path: '/admin', 
        navId: 'admin',
        subItems: [
            { text: 'Prompt Configuration', path: '/admin/prompt-configurations', navId: 'prompt-configurations' },
            { text: 'Content Management', path: '/admin/content-management', navId: 'content-management' },
            { text: 'User Management', path: '/admin/user-management', navId: 'user-management' },
            { text: 'Company Management', path: '/admin/company-management', navId: 'company-management' },
            { text: 'Role Management', path: '/admin/role-management', navId: 'role-management' },
            { text: 'AI Request Logs', path: '/admin/ai-logs', navId: 'ai-logs' },
            { text: 'Bulk Knowledge Base Uploads', path: '/admin/bulk-uploads', navId: 'bulk-uploads' },
            { text: 'Maintenance', path: '/admin/maintenance', navId: 'maintenance' }
        ]
    }
];

// Default navigation settings for new users.
export const defaultNavigationModules = ['dashboard', 'skills', 'advisor', 'assist', 'library'];

export const defaultNavigationSubModules = navigationData
    .filter(item => defaultNavigationModules.includes(item.navId)) // Filter for only the default top-level modules
    .flatMap(item => item.subItems || []) // Get sub-items
    .map(subItem => subItem.navId)      // Get navIds
    .filter((navId): navId is string => {
        if (!navId) return false;
        
        // Define explicitly restricted submodules for new users
        const restricted = [
            'certification-paths', 'deep-dive', 'course-management', 'news-management', // Skills & Studio restricted
            'strategic-account-planner', 'na-solution-builder' // Advisor restricted
        ];
        
        return !restricted.includes(navId);
    });

